/**
 * AI 评判服务
 */

import type { TranslationCard, ArgumentCard } from './types';
import { callOpenRouter } from '../../services/llm/openrouter';

export interface EvaluationResult {
  score: number;        // 0-100
  feedback: string;     // 简短反馈
  corrections?: string; // 修改建议
  keywordUsed?: string[]; // 用到的关键词
}

// 评判句子翻译
export async function evaluateTranslation(
  card: TranslationCard,
  userAnswer: string
): Promise<EvaluationResult> {
  const prompt = `你是一位严格的英语翻译评判老师。请评判【用户答案】的翻译质量。

【原文】${card.chinese}
【参考译文】${card.english}
【建议关键词】${card.keywords.join(', ')}
【用户答案】${userAnswer}

重要提示：
- 你必须评判的是【用户答案】，不是参考译文
- 如果用户答案明显不是英文句子、过于简短、或与原文完全无关，必须给0-20分
- 只有当用户答案正确翻译了原文意思，才能给高分

评分标准：意思准确(40分)、关键词使用(30分)、语法正确(30分)

输出 JSON（不要有其他文字）：
{
  "score": 0到100的整数,
  "feedback": "一句话评价用户答案的翻译质量",
  "corrections": "修改建议，无则为空字符串",
  "keywordUsed": ["用户答案中实际使用的关键词，没有则为空数组"]
}`;

  try {
    console.log('[evaluate] 发送评判请求，用户答案:', userAnswer);
    const result = await callOpenRouter(prompt, { jsonMode: true });
    console.log('[evaluate] AI 原始返回:', result);
    if (!result) throw new Error('AI 返回为空');
    
    const json = JSON.parse(result);
    console.log('[evaluate] 解析后 JSON:', json);
    return {
      score: json.score ?? 0,
      feedback: json.feedback ?? '评判失败',
      corrections: json.corrections || undefined,
      keywordUsed: json.keywordUsed || [],
    };
  } catch (err) {
    console.error('[evaluate] translation error:', err);
    return {
      score: 0,
      feedback: '评判服务暂时不可用',
    };
  }
}

// 评判要点组句
export async function evaluateArgument(
  card: ArgumentCard,
  userAnswer: string
): Promise<EvaluationResult> {
  const prompt = `你是一位严格的英语写作评判老师。请评判【用户答案】的写作质量。

【观点】${card.argument}
【必须使用的要点】${card.keyPoints.join(', ')}
【参考答案】${card.reference}
【用户答案】${userAnswer}

重要提示：
- 你必须评判的是【用户答案】，不是参考答案
- 如果用户答案明显不是英文句子、过于简短、或与观点完全无关，必须给0-20分
- 只有当用户答案正确围绕观点展开并使用了要点，才能给高分

评分标准：围绕观点(30分)、使用要点(40分)、表达通顺(30分)

输出 JSON（不要有其他文字）：
{
  "score": 0到100的整数,
  "feedback": "一句话评价用户答案的写作质量",
  "corrections": "改进建议，无则为空字符串",
  "keywordUsed": ["用户答案中实际使用的要点，没有则为空数组"]
}`;

  try {
    const result = await callOpenRouter(prompt, { jsonMode: true });
    if (!result) throw new Error('AI 返回为空');
    
    const json = JSON.parse(result);
    return {
      score: json.score ?? 0,
      feedback: json.feedback ?? '评判失败',
      corrections: json.corrections || undefined,
      keywordUsed: json.keywordUsed || [],
    };
  } catch (err) {
    console.error('[evaluate] argument error:', err);
    return {
      score: 0,
      feedback: '评判服务暂时不可用',
    };
  }
}
